import { useState, useEffect } from 'react';
import { useToast } from '../../components/shared/ToastNotification';
import api from '../../services/api';
import QuickRegistration from '../../components/customer/QuickRegistration';

interface Pause {
  id: string;
  pause_start_date: string;
  pause_end_date: string;
  reason?: string;
}

interface Subscription {
  id: string;
  customer: { id: string; name: string; phone?: string };
  product: { id: string; name: string; mrp?: number };
  delivery_route?: { id: string; name: string };
  quantity: number;
  frequency: string;
  status: string;
  delivery_time: string;
  unit_price?: number;
  start_date: string;
  end_date?: string;
  active_pause?: Pause | null;
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
  paused: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
  cancelled: 'bg-red-50 text-red-500 ring-1 ring-red-100',
};

const TIME_ICONS: Record<string, string> = { morning: '🌅', evening: '🌆' };

export default function DeliverySubscriptionsIndex() {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [products, setProducts] = useState<{ id: string; name: string; mrp?: number }[]>([]);
  const [routes, setRoutes] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [pauseModal, setPauseModal] = useState<{ subId: string; name: string } | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');

  // Customer selection state
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState<any[]>([]);
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);

  const [formData, setFormData] = useState({
    customer_id: '',
    product_id: '',
    delivery_route_id: '',
    quantity: 1,
    frequency: 'daily',
    status: 'active',
    delivery_time: 'morning',
    unit_price: '',
    start_date: new Date().toISOString().split('T')[0],
  });

  const [pauseData, setPauseData] = useState({
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    reason: '',
  });

  const { success, error } = useToast();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [subRes, prodRes, routeRes] = await Promise.all([
        api.get('/delivery_subscriptions'),
        api.get('/products'),
        api.get('/delivery_routes'),
      ]);

      if (subRes.success) {
        setSubscriptions(subRes.data.delivery_subscriptions || []);
      }
      
      if (prodRes.success) {
        setProducts(prodRes.data.products || prodRes.data.data || []);
      }

      if (routeRes.success) {
        setRoutes(routeRes.data.delivery_routes || []);
      }

      if (!subRes.success || !prodRes.success || !routeRes.success) {
        error(subRes.message || prodRes.message || routeRes.message || 'Failed to load some data');
      }
    } catch { 
      error('Failed to load data'); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleCustomerSearch = async (val: string) => {
    setCustomerSearch(val);
    if (val.length < 2) {
      setCustomerResults([]);
      setShowCustomerResults(false);
      return;
    }
    try {
      const res = await api.get(`/search/customers`, { q: val });
      if (res.success) {
        setCustomerResults(res.data.customers || []);
        setShowCustomerResults(true);
      }
    } catch (err) {
      console.error('Search failed', err);
    }
  };

  const selectCustomer = (c: any) => {
    setSelectedCustomer(c);
    setFormData({ ...formData, customer_id: c.id });
    setCustomerSearch(c.name);
    setShowCustomerResults(false);
    setShowNewCustomerForm(false);
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setFormData(prev => ({ ...prev, customer_id: '' }));
    setCustomerSearch('');
  };

  const onRegistrationSuccess = (customer: any) => {
    selectCustomer(customer);
    setShowNewCustomerForm(false);
    setCustomerSearch('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (!payload.unit_price) delete (payload as any).unit_price;
      const res = await api.post('/delivery_subscriptions', { delivery_subscription: payload });
      if (res.success || res.id) {
        success('Subscription created!');
        setShowForm(false);
        setFormData({
            customer_id: '',
            product_id: '',
            delivery_route_id: '',
            quantity: 1,
            frequency: 'daily',
            status: 'active',
            delivery_time: 'morning',
            unit_price: '',
            start_date: new Date().toISOString().split('T')[0],
        });
        setCustomerSearch('');
        setSelectedCustomer(null);
        fetchData();
      } else {
        error(res.errors?.join(', ') || res.message || 'Failed to create');
      }
    } catch { error('An error occurred'); }
  };

  const handleAction = async (subId: string, action: 'resume' | 'cancel') => {
    try {
      const res = await api.patch(`/delivery_subscriptions/${subId}/${action}`);
      if (res.success) {
        success(`Subscription ${action}d!`);
        fetchData();
      } else error(`Failed to ${action}`);
    } catch { error('An error occurred'); }
  };

  const handlePauseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pauseModal) return;
    try {
      const res = await api.patch(`/delivery_subscriptions/${pauseModal.subId}/pause`, pauseData);
      if (res.success) {
        success('Pause scheduled successfully');
        setPauseModal(null);
        fetchData();
      } else {
        error(res.errors?.join(', ') || res.message || 'Failed to schedule pause');
      }
    } catch { error('An error occurred'); }
  };

  const filtered = filterStatus === 'all' ? subscriptions : subscriptions.filter(s => s.status === filterStatus);

  return (
    <div className="space-y-6 text-slate-900">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Delivery Subscriptions</h1>
          <p className="text-slate-500 mt-0.5 text-sm">Manage recurring product deliveries — milk, water, tiffin</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            id="filter-status"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            id="new-subscription-btn"
            onClick={() => setShowForm(!showForm)}
            className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
          >
            {showForm ? 'Cancel' : '+ New Subscription'}
          </button>
        </div>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm animate-in fade-in slide-in-from-top-4">
          <h3 className="font-bold text-slate-700 mb-4 tracking-tight uppercase text-xs">New Subscription</h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              
              {/* Searchable Customer Input */}
              <div className="relative">
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Customer</label>
                <div className="space-y-4">
                    {!selectedCustomer ? (
                        <div className="relative group">
                            <input 
                                type="text" 
                                value={customerSearch}
                                onChange={(e) => handleCustomerSearch(e.target.value)}
                                placeholder="Find by name or email..."
                                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-sm text-slate-900"
                                autoComplete="off"
                            />
                            <button 
                                type="button"
                                onClick={() => {
                                    setShowNewCustomerForm(!showNewCustomerForm);
                                    setShowCustomerResults(false);
                                }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-white border border-slate-200 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors shadow-sm"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
                            </button>

                            {showCustomerResults && (
                                <div className="absolute z-50 left-0 right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-2xl max-h-64 overflow-y-auto p-2 scrollbar-hide">
                                    {customerResults.length > 0 ? (
                                        customerResults.map(c => (
                                            <button
                                                key={c.id}
                                                type="button"
                                                onClick={() => selectCustomer(c)}
                                                className="w-full text-left px-4 py-3 hover:bg-slate-50 rounded-xl flex items-center justify-between group transition-colors border-b border-slate-50 last:border-0"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center text-xs font-black shadow-sm ring-1 ring-indigo-100">
                                                        {c.name.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-slate-800 group-hover:text-indigo-600 transition-colors text-sm">{c.name}</div>
                                                        <div className="text-[10px] text-slate-400 font-bold tracking-tight uppercase">{c.phone || c.email || 'Retail Partner'}</div>
                                                    </div>
                                                </div>
                                                <svg className="w-4 h-4 text-slate-200 group-hover:text-indigo-400 transition-all opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        ))
                                    ) : (
                                        <div className="p-4 text-center">
                                            <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mb-3 italic">No results found</p>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowNewCustomerForm(true);
                                                    setShowCustomerResults(false);
                                                }}
                                                className="w-full py-2.5 bg-slate-900 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-black transition-all shadow-sm"
                                            >
                                                + Quick Register "{customerSearch}"
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center justify-between p-3 bg-indigo-50/40 border border-indigo-100 rounded-xl animate-in zoom-in-95 duration-200">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 bg-white border border-indigo-100 rounded-lg flex items-center justify-center text-indigo-600 font-bold shadow-sm ring-1 ring-indigo-50/50">
                                    {selectedCustomer.name.charAt(0)}
                                </div>
                                <div>
                                    <p className="font-bold text-slate-900 text-sm leading-tight">{selectedCustomer.name}</p>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{selectedCustomer.phone || selectedCustomer.email || 'Retail Partner'}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={clearCustomer}
                                className="p-1.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-all"
                                title="Clear selection"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}

                    {showNewCustomerForm && (
                        <QuickRegistration 
                            initialName={customerSearch}
                            onSuccess={onRegistrationSuccess}
                            onCancel={() => setShowNewCustomerForm(false)}
                        />
                    )}
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Product</label>
                <select id="sub-product" value={formData.product_id} onChange={e => setFormData({ ...formData, product_id: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-sm h-[46px] text-slate-900" required>
                  <option value="" className="text-slate-900">Select Product</option>
                  {products.map(p => <option key={p.id} value={p.id} className="text-slate-900">{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Route (optional)</label>
                <select id="sub-route" value={formData.delivery_route_id} onChange={e => setFormData({ ...formData, delivery_route_id: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-sm h-[46px]">
                  <option value="">Standalone</option>
                  {routes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Daily Qty</label>
                <input id="sub-quantity" type="number" value={formData.quantity} min="0.1" step="0.1"
                  onChange={e => setFormData({ ...formData, quantity: Number(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-sm text-slate-900" required />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Unit Price (₹)</label>
                <input id="sub-price" type="number" value={formData.unit_price} min="0" step="0.50" placeholder="Auto from product"
                  onChange={e => setFormData({ ...formData, unit_price: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-sm text-slate-900" />
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Delivery Time</label>
                <select id="sub-time" value={formData.delivery_time} onChange={e => setFormData({ ...formData, delivery_time: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-sm h-[46px]">
                  <option value="morning">🌅 Morning</option>
                  <option value="evening">🌆 Evening</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Frequency</label>
                <select id="sub-freq" value={formData.frequency} onChange={e => setFormData({ ...formData, frequency: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-sm h-[46px]">
                  <option value="daily">Daily</option>
                  <option value="alternate">Alternate Days</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Start Date</label>
                <input id="sub-start" type="date" value={formData.start_date}
                  onChange={e => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-sm text-slate-900" required />
              </div>
            </div>
            <div className="flex justify-end pt-6 border-t border-slate-50">
              <button id="sub-submit" type="submit"
                className="px-10 py-3.5 bg-slate-900 text-white rounded-xl font-bold uppercase tracking-widest text-xs hover:bg-black transition-all shadow-xl shadow-slate-200">
                Create Subscription
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-slate-50/50 border-b border-slate-100">
              {['Customer', 'Product', 'Qty / Price', 'Time & Freq', 'Route', 'Status', 'Actions'].map(h => (
                <th key={h} className="px-5 py-4 text-[10px] font-black uppercase text-slate-400 tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {loading ? (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-slate-400 uppercase text-[10px] font-bold tracking-widest">Loading subscriptions...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-slate-400 uppercase text-[10px] font-bold tracking-widest italic">No subscriptions found.</td></tr>
            ) : filtered.map(sub => (
              <tr key={sub.id} className="hover:bg-slate-50/60 transition-colors group">
                <td className="px-5 py-4">
                  <div className="font-bold text-slate-800 text-sm tracking-tight">{sub.customer?.name}</div>
                  {sub.customer?.phone && <div className="text-[11px] text-slate-400 font-bold tracking-tight">{sub.customer.phone}</div>}
                </td>
                <td className="px-5 py-4 text-slate-600 text-xs font-bold">{sub.product?.name}</td>
                <td className="px-5 py-4">
                  <div className="font-black text-indigo-600 text-sm">{sub.quantity} units</div>
                  <div className="text-[11px] text-slate-400 font-bold">
                    ₹{sub.unit_price ?? sub.product?.mrp ?? '—'} / unit
                  </div>
                </td>
                <td className="px-5 py-4">
                  <div className="text-xs font-bold text-slate-700">{TIME_ICONS[sub.delivery_time] || ''} {sub.delivery_time}</div>
                  <div className="text-[11px] text-slate-400 font-bold capitalize">{sub.frequency}</div>
                </td>
                <td className="px-5 py-4">
                  {sub.delivery_route ? (
                    <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-tight">{sub.delivery_route.name}</span>
                  ) : <span className="text-slate-300 text-xs">—</span>}
                </td>
                <td className="px-5 py-4">
                  <div className="flex flex-col gap-1">
                    <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase w-fit ${STATUS_COLORS[sub.status] || 'bg-slate-100 text-slate-500'}`}>
                      {sub.status}
                    </span>
                    {sub.active_pause && (
                      <span className="text-[10px] text-amber-600 font-bold">
                        Paused until {new Date(sub.active_pause.pause_end_date).toLocaleDateString('en-IN')}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-1">
                    {sub.status === 'active' && (
                      <button
                        onClick={() => setPauseModal({ subId: sub.id, name: sub.customer?.name })}
                        className="px-2 py-1 text-[10px] font-black text-amber-600 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors tracking-tight uppercase"
                        title="Schedule a pause"
                      >
                        Pause
                      </button>
                    )}
                    {sub.status === 'paused' && (
                      <button
                        onClick={() => handleAction(sub.id, 'resume')}
                        className="px-2 py-1 text-[10px] font-black text-emerald-600 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors tracking-tight uppercase"
                      >
                        Resume
                      </button>
                    )}
                    {sub.status !== 'cancelled' && (
                      <button
                        onClick={() => { if (confirm('Cancel this subscription?')) handleAction(sub.id, 'cancel'); }}
                        className="px-2 py-1 text-[10px] font-black text-red-500 bg-red-50 rounded-lg hover:bg-red-100 transition-colors tracking-tight uppercase"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pause Modal */}
      {pauseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-md p-8 animate-in fade-in zoom-in-95">
            <h3 className="font-black text-slate-800 text-xl tracking-tight mb-1">Schedule Pause</h3>
            <p className="text-slate-500 text-sm font-medium mb-6">Pausing deliveries for <strong className="text-indigo-600">{pauseModal.name}</strong></p>
            <form onSubmit={handlePauseSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">From</label>
                  <input id="pause-start" type="date" value={pauseData.start_date}
                    onChange={e => setPauseData({ ...pauseData, start_date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-sm" required />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">To</label>
                  <input id="pause-end" type="date" value={pauseData.end_date}
                    onChange={e => setPauseData({ ...pauseData, end_date: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-sm" required />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Reason (optional)</label>
                <input id="pause-reason" type="text" value={pauseData.reason} placeholder="e.g. Vacation, Festival"
                  onChange={e => setPauseData({ ...pauseData, reason: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-bold text-sm" />
              </div>
              <div className="flex gap-3 pt-6">
                <button type="button" onClick={() => setPauseModal(null)}
                  className="flex-1 px-4 py-3.5 border border-slate-200 text-slate-500 rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-slate-50">
                  Cancel
                </button>
                <button id="pause-submit" type="submit"
                  className="flex-1 px-4 py-3.5 bg-amber-500 text-white rounded-xl font-black uppercase tracking-widest text-[10px] shadow-xl shadow-amber-100 hover:bg-amber-600 transition-all">
                  Schedule Pause
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
